import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Layers, FlaskConical, Link2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SectionLabel from '../components/SectionLabel';
import Tag from '../components/Tag';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] },
});

const researchAreas = [
  {
    title: 'Expressive Meaning & Emotional Valence',
    description:
      'A central thread of my work concerns how emotional valence shapes the interpretation of expressive language. I investigate how expressive adjectives (e.g. "damn", "poor") are interpreted depending on the emotional charge of their context — including how negativity, blame attribution, and affective agreement between expressions and their referents drive meaning construction. This work sits at the intersection of formal semantics and experimental pragmatics.',
    keywords: ['Expressive adjectives', 'Valence agreement', 'Anaphora resolution', 'Culprit hypothesis'],
  },
  {
    title: 'Derogatory Language: Insults & Slurs',
    description:
      'My work sits at the intersection of experimental semantics, experimental pragmatics, and cognitive and social psychology — with a focus on how emotional and evaluative meaning is encoded, processed, and perceived in natural language.',
    keywords: ['Particularistic insults', 'Slurs', 'Gender pejoratives', 'Offense perception'],
  },
  {
    title: 'Experimental Semantics & Pragmatics',
    description:
      'Across all my research, I use experimental methods to test theoretically motivated hypotheses about meaning. I design and implement behavioral studies — including forced-choice tasks, self-paced reading studies, acceptability judgment tasks, and person-selection paradigms — using the Magpie framework. This empirical grounding allows me to bring psycholinguistic evidence to bear on questions in formal semantics and pragmatics.',
    keywords: ['Magpie', 'Self-paced reading', 'Forced-choice tasks', 'Acceptability judgments'],
  },
  {
    title: 'Exclamatives & Non-Canonical Constructions',
    description:
      'A secondary research interest concerns the felicity conditions of exclamative constructions, particularly wh-exclamatives in German. I examine what contextual and semantic factors license the use of these constructions, combining formal pragmatic analysis with experimental evidence from acceptability judgment studies.',
    keywords: ['Wh-exclamatives', 'Felicity conditions', 'German syntax', 'Formal pragmatics'],
  },
];

const currentProjects = [
  {
    title: 'Intelligence-Oriented Pejoratives at the Boundary Between Insult and Slur',
    description:
      'This project investigates expressions like "idiot" or "moron" that target perceived intellectual ability. I argue these occupy a theoretically interesting position between particularistic insults — which target individuals — and slurs, which target groups. The project combines corpus analysis with experimental data to characterize their semantic profile.',
    status: 'Submitted',
  },
  {
    title: 'Gender Pejoratives as Particularistic Insults',
    description:
      'Building on my broader work on derogatory language, this project examines whether gendered pejoratives like "bitch" or "Schlampe" function as group-targeting slurs or as context-dependent particularistic insults. Using self-paced reading and person-selection experiments, I test predictions about their processing and perceived targets.',
    status: 'In progress',
  },
  {
    title: 'Valence-Based Approach to Expressive Meaning',
    description:
      'This project develops a unified theoretical account of expressive meaning grounded in emotional valence. Rather than treating expressives as a heterogeneous class, I propose that valence — positive or negative emotional charge — is the key dimension that unifies their behavior across syntactic and pragmatic contexts.',
    status: 'In progress',
  },
];

const relatedLinks = [
  { label: 'Publications', link: '/publications' },
  { label: 'Experiments', link: '/experiments' },
  { label: 'Conferences', link: '/conferences' },
];

const Research = () => (
  <div>
    <PageHeader
      eyebrow="Kalle Glauch"
      title="Research"
      lead="My work sits at the intersection of formal semantics, experimental pragmatics, and social psychology — with a focus on how emotional and evaluative meaning is encoded, processed, and perceived in natural language."
    />

    <div className="mx-auto max-w-5xl px-6 py-14 md:px-10 md:py-16">
      {/* Research areas */}
      <SectionLabel icon={Layers}>Research Areas</SectionLabel>
      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
        {researchAreas.map((area, i) => (
          <motion.article
            key={area.title}
            {...fadeUp(i * 0.05)}
            className="rounded-md border border-line bg-beige-50 p-7"
          >
            <h3 className="text-base font-semibold leading-snug text-ink-900">{area.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-ink-600">{area.description}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {area.keywords.map((kw) => (
                <Tag key={kw}>{kw}</Tag>
              ))}
            </div>
          </motion.article>
        ))}
      </div>

      {/* Current projects */}
      <div className="mt-16 border-t border-line pt-12">
        <SectionLabel icon={FlaskConical}>Current Projects</SectionLabel>
        <div className="mt-6 space-y-4">
          {currentProjects.map((project, i) => (
            <motion.article
              key={project.title}
              {...fadeUp(i * 0.05)}
              className="rounded-md border border-line bg-beige-50 p-7"
            >
              <div className="mb-2 flex items-start justify-between gap-4">
                <h3 className="text-base font-semibold leading-snug text-ink-900">{project.title}</h3>
                <span className="flex-shrink-0 whitespace-nowrap pt-1 text-[11px] font-semibold uppercase tracking-label text-navy-600">
                  {project.status}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-ink-600">{project.description}</p>
            </motion.article>
          ))}
        </div>
      </div>

      {/* Related */}
      <div className="mt-16 border-t border-line pt-12">
        <SectionLabel icon={Link2}>Related</SectionLabel>
        <div className="mt-6 flex flex-wrap gap-3">
          {relatedLinks.map((item) => (
            <Link
              key={item.link}
              to={item.link}
              className="flex items-center gap-2 rounded-md bg-navy-900 px-5 py-2.5 text-sm font-medium text-[color:var(--on-navy)] transition-colors hover:bg-navy-800"
            >
              {item.label}
              <ArrowRight size={15} />
            </Link>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default Research;
